import { API_ORIGIN } from "./apiOrigin";
import axios from "axios";

const BASE_URL = API_ORIGIN;

export const api = axios.create({
  baseURL: `${BASE_URL}/api`,
  withCredentials: true,
});

const TOKEN_KEY = "dapnow_token";

export const tokenStore = {
  get() {
    return localStorage.getItem(TOKEN_KEY);
  },
  set(token) {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY);
  },
};

api.interceptors.request.use((config) => {
  const token = tokenStore.get();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Phase 44.31.1 — Response interceptor: 401 fatale → broadcast "auth:revoked".
// Risolve "zombie session": qualsiasi endpoint che risponde 401 con
// detail riconducibile a sessione/token invalida fa logout immediato
// in tutta la SPA (AuthProvider ascolta l'evento e fa redirect+toast).
//
// Comportamento:
//   - 401 + detail = "Sessione revocata, accedi di nuovo." → revoked
//   - 401 + detail = "Token scaduto" → expired
//   - 401 + detail = "Token non valido" / "Non autenticato" → invalid
//   - 401 su /auth/login o /auth/register → IGNORATO (è una credenziale errata)
//   - 401 su /auth/me iniziale → IGNORATO (fetchMe gestisce già il null user)
const _AUTH_BYPASS_PATHS = ["/auth/login", "/auth/register", "/auth/forgot-password", "/auth/reset-password"];
api.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const status = error?.response?.status;
      const reqUrl = (error?.config?.url || "").toString();
      if (status === 401 && !_AUTH_BYPASS_PATHS.some((p) => reqUrl.includes(p))) {
        const detail = error.response?.data?.detail;
        const detailStr = typeof detail === "string" ? detail.toLowerCase() : "";
        let reason = "invalid";
        if (detailStr.includes("revocata") || detailStr.includes("revoked")) reason = "revoked";
        else if (detailStr.includes("scaduto") || detailStr.includes("expired")) reason = "expired";
        // Clear token immediatamente
        if (tokenStore.get()) {
          tokenStore.clear();
          window.dispatchEvent(new CustomEvent("auth:revoked", { detail: { reason } }));
        }
      }
    } catch { /* noop */ }
    return Promise.reject(error);
  }
);

/**
 * Phase 43.40 — Estrae un messaggio utente chiaro da un errore axios.
 * Gestisce:
 *  - errori di rete / CORS / backend irraggiungibile (no `error.response`)
 *  - status code HTTP comuni (401, 403, 404, 409, 422, 429, 5xx)
 *  - detail string o array (Pydantic)
 * Restituisce sempre una stringa user-friendly, MAI vuota.
 */
export function extractAxiosErrorMessage(error, context = "default") {
  // 1) Network error / timeout / CORS → niente response
  if (error && !error.response) {
    if (error.code === "ECONNABORTED") return "La richiesta ha impiegato troppo tempo. Riprova.";
    if (error.message === "Network Error") return "Servizio momentaneamente non raggiungibile. Controlla la connessione e riprova.";
    return "Servizio momentaneamente non disponibile. Riprova tra qualche istante.";
  }
  const status = error.response?.status;
  const detail = error.response?.data?.detail;
  // 2) Detail esplicito dal backend (string o pydantic array) → usalo
  const fromDetail = detail != null ? formatApiError(detail) : null;
  if (fromDetail && fromDetail !== "Si è verificato un errore. Riprova.") return fromDetail;
  // 3) Fallback per status code, customizzato per context
  if (context === "login") {
    if (status === 401 || status === 403) return "Email o password non corretti.";
    if (status === 429) return "Troppi tentativi di accesso. Riprova tra qualche minuto.";
    if (status === 404) return "Servizio di login non disponibile. Contatta il supporto.";
    if (status >= 500) return "Servizio momentaneamente non disponibile. Riprova tra qualche istante.";
  }
  if (context === "register") {
    if (status === 409) return "Esiste già un account con questa email.";
    if (status === 422) return "Dati non validi: controlla email, password e telefono.";
    if (status >= 500) return "Servizio momentaneamente non disponibile. Riprova tra qualche istante.";
  }
  if (status === 401) return "Sessione scaduta. Esci e accedi di nuovo.";
  if (status === 403) return "Non hai i permessi per questa azione.";
  if (status === 404) return "Risorsa non trovata.";
  if (status >= 500) return "Errore del servizio. Riprova tra qualche istante.";
  return fromDetail || "Si è verificato un errore. Riprova.";
}

function structuredErrorMessage(detail) {
  // Keep legacy `msg` precedence; newer API guards use { code, message }.
  for (const key of ["msg", "message"]) {
    if (typeof detail[key] === "string" && detail[key].trim()) return detail[key];
  }
  return "Si è verificato un errore. Riprova.";
}

export function formatApiError(detail) {
  if (detail == null) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // Pydantic-style: [{loc, msg, type, ...}, ...] — formatta in italiano
    const messages = detail.map((e) => {
      if (!e || typeof e !== "object") return String(e);
      const field = Array.isArray(e.loc) ? e.loc.filter((p) => p !== "body").join(".") : null;
      const msg = structuredErrorMessage(e);
      // Traduzione naive di pochi messaggi comuni di Pydantic
      let friendly = msg
        .replace(/^Field required$/i, "campo obbligatorio mancante")
        .replace(/^Input should be a valid string$/i, "valore non valido (stringa attesa)")
        .replace(/^Input should be a valid (number|integer)$/i, "valore non valido (numero atteso)")
        .replace(/String should have at most (\d+) character/i, "lunghezza massima: $1 caratteri")
        .replace(/String should have at least (\d+) character/i, "lunghezza minima: $1 caratteri")
        .replace(/Input should be (.+)$/i, "valore atteso: $1");
      return field ? `${field}: ${friendly}` : friendly;
    });
    return messages.join(" · ");
  }
  if (typeof detail === "object") return structuredErrorMessage(detail);
  return String(detail);
}
